import { GridValueFormatterParams } from '@mui/x-data-grid';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { ContextClientInsurerCollectionType } from '../../context/billing.context';
import {
  BillSubmissionsType,
  InternalHoldBaseType,
  PartnershipType,
  SubmissionRemitsType,
  SubmissionStatusEnum,
} from '../../types';
import {
  capitalizeFirstLetterAndSpaceCamelCaseString,
  capitalizeFirstLetterAndSpaceSnakeString,
} from '../../utils/bills-utils';
import {
  BillTypeDataList,
  ClientInsurerPartnershipData,
  EligibilityCheckHoldReasons,
  ReleasableHoldReasons,
} from './bill-grid-components/bill-management-types';

import { computeEffectiveRange } from '../../utils/date-helper';

// Client/insurer details are loaded by a separate batch query (ciData), keyed by the
// contract's clientsInsurerId.
//   PENDING    — contract resolved, its ciData entry hasn't arrived → show "Loading..."
//   UNRESOLVED — no contract, so nothing will ever be fetched → settle to "--"
const PENDING_CLIENT_INSURER = {
  client: null,
  insurer: null,
  billingType: null,
  loading: true,
  error: null,
};
const UNRESOLVED_CLIENT_INSURER = {
  ...PENDING_CLIENT_INSURER,
  loading: false,
};

export const generateEnrichedData = <
  T extends {
    clientsInsurerId?: number | null;
    partnershipId?: number | null;
    contractId?: number | null;
  },
>(
  items: T[],
  ciData: ContextClientInsurerCollectionType,
  partnershipsData: PartnershipType[],
  contractsData: Array<{
    id: number;
    clientsInsurerId: number;
    partnershipId?: number | null;
    billingType?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  }>,
): (T & ClientInsurerPartnershipData)[] => {
  const partnershipsMap = new Map(partnershipsData.map(p => [p.id, p]));
  const contractsMap = new Map(contractsData.map(c => [c.id, c]));

  return items.map(item => {
    const contract = contractsMap.get(item.contractId ?? 0);
    const resolvedCiId = contract?.clientsInsurerId ?? 0;

    const ci = contract
      ? (ciData[resolvedCiId] ?? PENDING_CLIENT_INSURER)
      : UNRESOLVED_CLIENT_INSURER;

    const partnership = partnershipsMap.get(contract?.partnershipId ?? 0) ?? {
      id: null,
      name: null,
    };

    const isResolved = !ci.loading && !ci.error;

    const billingType = contract?.billingType;

    const contractDateRange =
      computeEffectiveRange(
        contract?.startDate ?? '',
        contract?.endDate ?? '',
      ) ?? null;

    return {
      ...item,
      clientsInsurerId: resolvedCiId,
      client: {
        name: isResolved ? (ci.client?.name ?? null) : null,
        id: isResolved ? (ci.client?.id ?? null) : null,
        isErrored: Boolean(ci.error),
        loading: ci.loading,
      },
      insurer: {
        name: isResolved ? (ci.insurer?.name ?? null) : null,
        id: isResolved ? (ci.insurer?.id ?? null) : null,
        isErrored: Boolean(ci.error),
        loading: ci.loading,
      },
      partnership: {
        name: partnership.name,
        id: partnership.id,
      },
      billingType: {
        value: billingType ?? null,
      },
      contractDateRange: {
        value: contractDateRange ?? null,
      },
    };
  });
};

export const gridValueDisplayGenerator = ({
  value,
}: GridValueFormatterParams): string => {
  if (value.name) {
    return value.name;
  }
  if (value.value) {
    return value.value;
  }
  if (value.error) {
    return 'Error';
  }
  if (value.loading) {
    return 'Loading...';
  }
  return '--';
};

export const hasReleasableHoldTypes = (
  selectedRows: BillTypeDataList,
): boolean =>
  selectedRows.every(row =>
    Object.values(ReleasableHoldReasons).includes(
      row.holdDetails?.holdType as ReleasableHoldReasons,
    ),
  );

export const hasAllowedEligibilityCheckHolds = (
  selectedRows: BillTypeDataList,
): boolean =>
  selectedRows.every(row =>
    Object.values(EligibilityCheckHoldReasons).includes(
      row.holdDetails?.holdType as EligibilityCheckHoldReasons,
    ),
  );

export const doesClientInsurerChangeMatch = (
  selectedRows: BillTypeDataList,
): boolean => {
  const hasSameHoldType = selectedRows.every(
    row => row.holdDetails?.holdType === selectedRows[0].holdDetails?.holdType,
  );
  if (hasSameHoldType && selectedRows.length > 0) {
    if (
      selectedRows[0].holdDetails?.holdType ===
      ReleasableHoldReasons.CLIENT_CHANGE
    ) {
      // ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
      return selectedRows.every(
        row =>
          row.holdDetails?.clientId === selectedRows[0].holdDetails?.clientId,
      );
    } else {
      // ACCOUNTS-AND-PLAN-MIGRATION: insurerId is insurer-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
      return selectedRows.every(
        row =>
          row.holdDetails?.insurerId === selectedRows[0].holdDetails?.insurerId,
      );
    }
  }
  return false;
};

export const displayBillFilterStatus = (
  internalHolds: InternalHoldBaseType[],
  submissions: SubmissionRemitsType[],
  status: string,
): string => {
  const activeHold = internalHolds.filter(hold => hold.clearedAt === null);
  const nonVoidedSubmissions = submissions.filter(
    submission => submission.status !== SubmissionStatusEnum.Void.toLowerCase(),
  );
  if (activeHold.length > 0) {
    return 'Held';
  } else if (nonVoidedSubmissions.length > 0) {
    return 'Submitted';
  } else {
    return capitalizeFirstLetterAndSpaceSnakeString(status);
  }
};

/**
 * Returns the row count for the DataGrid when the backend does not provide a total count.
 * - If number of rows on page less than the page size, then this is the last page,
 *   return the count of rows up to this page (pageSize * page + gridRowsCount).
 * - If number of rows is equal to or greater than the page size, there may be more rows,
 *   return -1 for DataGrid to show that there are more pages
 */
export const getGridCount = (
  gridRowsCount: number,
  paginationModel: GridPaginationModel,
): number =>
  gridRowsCount < paginationModel.pageSize
    ? gridRowsCount + paginationModel.pageSize * paginationModel.page
    : -1;

export const parseIds = (ids: (number | string)[]): number[] =>
  ids
    .map(id => (typeof id === 'number' ? id : parseInt(id)))
    .filter(id => !isNaN(id));

export const formatDateForQuery = (date: Date | null): string | null =>
  dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : null;

export const extractIds = <T extends { id: number }>(items: T[]): number[] =>
  items.map(item => item.id);

interface PriceAdjustmentRecord {
  delta: number;
  detailId?: number | null;
  appliedAt: string;
}

export interface FormattedPriceAdjustment {
  key: string;
  label: string;
  amount: number;
  appliedAt: string;
}

interface PriceAdjustmentResult {
  adjustmentEntries: FormattedPriceAdjustment[];
  isBillAmountValid: boolean;
}

export interface BillAmountValidationInput {
  baseCharge: number;
  billAmount?: number | null;
  details?: { priceAdjustments?: Record<string, { delta: number }> } | null;
}

/**
 * Validates that billAmount equals baseCharge + sum of price adjustment deltas
 * (within 0.01 tolerance). Returns true (skips validation) when details is null,
 * since older records may not have details populated.
 */
export const checkBillAmountValidity = (
  bill: BillAmountValidationInput,
): boolean => {
  if (!bill.details) {
    return true;
  }

  const priceAdjustments = bill.details.priceAdjustments;

  if (!priceAdjustments || Object.keys(priceAdjustments).length === 0) {
    return (
      typeof bill.billAmount === 'number' && bill.billAmount === bill.baseCharge
    );
  }

  const totalAdjustment = Object.values(priceAdjustments).reduce(
    (sum, adj) => sum + adj.delta,
    0,
  );

  return (
    typeof bill.billAmount === 'number' &&
    Math.abs(bill.billAmount - (bill.baseCharge + totalAdjustment)) < 0.01
  );
};

/**
 * Processes price adjustments by formatting labels, sorting by application time,
 * and validating the final bill amount.
 *
 * @param billDetails - The bill details containing baseCharge, billAmount, and optional priceAdjustments
 * @returns Object containing:
 *          - adjustmentEntries: Array of formatted adjustments with human-readable labels,
 *            sorted chronologically by appliedAt timestamp (oldest first)
 *          - totalAdjustment: Sum of all adjustment amounts (typically negative values)
 *          - isValid: true if billAmount matches baseCharge + totalAdjustment (within 0.01 tolerance),
 *            or if there are no price adjustments to validate against
 */
export const processPriceAdjustments = (
  billDetails: BillSubmissionsType,
): PriceAdjustmentResult => {
  const priceAdjustments = billDetails.details?.priceAdjustments;

  if (!priceAdjustments || Object.keys(priceAdjustments).length === 0) {
    return {
      adjustmentEntries: [],
      isBillAmountValid: checkBillAmountValidity(billDetails),
    };
  }

  const adjustmentEntries = Object.entries(priceAdjustments)
    .map(([key, adjustment]) => ({
      key,
      label: capitalizeFirstLetterAndSpaceCamelCaseString(key),
      amount: (adjustment as PriceAdjustmentRecord).delta,
      appliedAt: (adjustment as PriceAdjustmentRecord).appliedAt,
    }))
    .sort((a, b) => dayjs(a.appliedAt).diff(dayjs(b.appliedAt)));

  return {
    adjustmentEntries,
    isBillAmountValid: checkBillAmountValidity(billDetails),
  };
};
